/**
 * Pure resolvers that turn QuickstartAnswers into the concrete inputs the
 * `useFunnelCreation` hook needs:
 *
 *   1. `resolveTemplateId`     — picks which funnel template to create
 *   2. `resolveStarterOverrides` — picks per-stepType starter overrides
 *      (e.g. BOGO checkout swap)
 *   3. `buildPrepareFunnel`    — async callback that mutates+persists the
 *      freshly created funnel based on the offer + payment answers
 *
 * Keeping these out of the React component tree makes them trivial to
 * unit-test and lets the wizard stay declarative.
 */

import type { QuickstartAnswers } from './wizard-steps/types';
import type { CreatedFunnel, FunnelNodeLite } from './useFunnelCreation';

// ============================================================================
// Q1 + Q3 → template id
//
// For the landing source we pick between `default` (checkout → thankyou) and
// `with-offer` (checkout → upsell → thankyou) based on whether the merchant
// wants an upsell at all. For Shopify / Woo we always start from the
// integration-specific template and inject the upsell node ourselves in
// `applyOfferStrategy` — those templates don't ship with one.
// ============================================================================

export function resolveTemplateId(answers: QuickstartAnswers): string | null {
    if (!answers.trafficSource) return null;
    if (answers.trafficSource === 'shopify') return 'shopify-default';
    if (answers.trafficSource === 'woocommerce') return 'woocommerce-default';
    // landing — pick the upsell-enabled flow when any offer strategy other
    // than "none" is selected; the offer transform fills in the variants.
    return answers.offerStrategy === 'none' || answers.offerStrategy === null
        ? 'default'
        : 'with-offer';
}

// ============================================================================
// Q2 → starter overrides
// ============================================================================

const BOGO_CHECKOUT_STARTER_ID = 'bogo-checkout';
const MANUAL_PAYMENT_THANKYOU_STARTER_ID = 'manual-payment-thankyou-page';

export function resolveStarterOverrides(answers: QuickstartAnswers): Record<string, string> {
    const overrides: Record<string, string> = {};
    if (answers.checkoutStyle === 'bogo') {
        overrides.checkout = BOGO_CHECKOUT_STARTER_ID;
    }
    // Manual mode (Zelle / custom payment) needs a thank-you that handles the
    // waiting-for-payment state and merchant instructions. The dedicated
    // starter switches between waiting and paid views based on order status.
    if (answers.paymentMode === 'manual' && answers.manualPaymentMethods.length > 0) {
        overrides.thankyou = MANUAL_PAYMENT_THANKYOU_STARTER_ID;
    }
    // The `landing` category default resolves to basic-landing.json (the only
    // landing starter flagged isDefault). Branded landing variants like
    // velora-landing remain reachable from the Studio template picker.
    return overrides;
}

// ============================================================================
// Q3 + Q4 → prepareFunnel callback
// ============================================================================

const OFFER_STEP_TYPES = new Set(['offer', 'upsell', 'downsell']);

interface BuildPrepareFunnelDeps {
    storeId: string;
    persistFunnel: (params: {
        funnelId: string;
        storeId: string;
        config: NonNullable<CreatedFunnel['config']>;
    }) => Promise<unknown>;
}

/**
 * Build the prepareFunnel callback handed to useFunnelCreation. The callback
 * is a no-op when no transforms apply (offer = single/none-but-no-offer-node
 * exists, payment = standard, etc.) — in that case it just returns the input.
 */
export function buildPrepareFunnel(
    answers: QuickstartAnswers,
    deps: BuildPrepareFunnelDeps,
): ((funnel: CreatedFunnel) => Promise<CreatedFunnel>) | undefined {
    // `single` matters too — for Shopify / Woo we need to inject the upsell
    // step, since those templates don't ship with one. Landing + single is a
    // no-op (the resolveTemplateId path already picked `with-offer`), so
    // running applyOfferStrategy there is harmless.
    const needsOfferTransform = answers.offerStrategy === 'none'
        || answers.offerStrategy === 'single'
        || answers.offerStrategy === 'geo'
        || answers.offerStrategy === 'split';
    const needsPaymentTransform = answers.paymentMode === 'manual'
        && answers.manualPaymentMethods.length > 0;
    const needsExtraMounts = answers.wantsClub === true || answers.wantsPortal === true;
    const needsLandingPrefix = answers.trafficSource === 'landing';

    if (!needsOfferTransform && !needsPaymentTransform && !needsExtraMounts && !needsLandingPrefix) {
        return undefined;
    }

    return async (funnel) => {
        let nextConfig = funnel.config ?? {};

        if (needsLandingPrefix) {
            nextConfig = applyLandingPrefix(nextConfig);
        }
        if (needsOfferTransform) {
            nextConfig = applyOfferStrategy(nextConfig, answers);
        }
        if (needsPaymentTransform) {
            nextConfig = applyManualPaymentMethods(nextConfig, answers.manualPaymentMethods);
        }
        if (needsExtraMounts) {
            nextConfig = applyExtraMountSteps(nextConfig, answers);
        }

        const next: CreatedFunnel = { ...funnel, config: nextConfig };

        await deps.persistFunnel({
            funnelId: funnel.id,
            storeId: deps.storeId,
            config: nextConfig,
        });

        return next;
    };
}

// ============================================================================
// Offer transforms
// ============================================================================

function applyOfferStrategy(
    config: NonNullable<CreatedFunnel['config']>,
    answers: QuickstartAnswers,
): NonNullable<CreatedFunnel['config']> {
    let working = config;
    const wantsOffer = answers.offerStrategy === 'single'
        || answers.offerStrategy === 'geo'
        || answers.offerStrategy === 'split';

    // Make sure the funnel has an upsell node to operate on. The landing-source
    // template path already picks `with-offer` when an upsell is requested,
    // so this only fires for Shopify / Woo templates which never ship one.
    if (wantsOffer && !hasOfferNode(working)) {
        working = injectOfferNode(working);
    }

    const nodes: FunnelNodeLite[] = working.nodes ?? [];
    const offerNodes = nodes.filter((n) => OFFER_STEP_TYPES.has(n.type));

    if (offerNodes.length === 0) return working;

    if (answers.offerStrategy === 'none') {
        return stripOfferNodes(working, offerNodes);
    }

    // geo / split are NOT applied here. We need the upsell node to be
    // auto-deployed first so each variant can inherit the deployed plugin
    // config (otherwise the funnel validator rejects the save with
    // "variant missing page configuration"). The wizard runs
    // `applyOfferSplitPostDeploy` after the deploy queue is done.
    return working;
}

/**
 * Post-deploy variant split. Run by the wizard once the auto-deploy queue
 * has finished filling the upsell node's `config` with a real plugin
 * deployment. Each variant inherits the deployed config so the funnel
 * validator passes; merchants then customize per-variant from the funnel
 * editor.
 *
 * Returns the unchanged config when there's nothing to split.
 */
export function applyOfferSplitPostDeploy(
    config: NonNullable<CreatedFunnel['config']> | undefined,
    answers: QuickstartAnswers,
): NonNullable<CreatedFunnel['config']> | undefined {
    if (!config) return config;
    if (answers.offerStrategy !== 'geo' && answers.offerStrategy !== 'split') {
        return config;
    }

    const nodes: FunnelNodeLite[] = config.nodes ?? [];
    const offerNodes = nodes.filter((n) => OFFER_STEP_TYPES.has(n.type));
    if (offerNodes.length === 0) return config;

    if (answers.offerStrategy === 'geo' && answers.geoCountries.length > 0) {
        const equalShare = Math.floor(100 / answers.geoCountries.length);
        return splitOfferNodes(config, offerNodes, {
            splitType: 'geo',
            variants: answers.geoCountries.map((country, index) => ({
                label: country,
                percentage: equalShare,
                regions: [country],
                indexHint: index,
            })),
        });
    }

    if (answers.offerStrategy === 'split') {
        return splitOfferNodes(config, offerNodes, {
            splitType: 'weighted',
            variants: [
                { label: 'A', percentage: 50, indexHint: 0 },
                { label: 'B', percentage: 50, indexHint: 1 },
            ],
        });
    }

    return config;
}

function hasOfferNode(config: NonNullable<CreatedFunnel['config']>): boolean {
    const nodes: FunnelNodeLite[] = config.nodes ?? [];
    return nodes.some((n) => OFFER_STEP_TYPES.has(n.type));
}

/**
 * Insert an upsell node between the checkout step and whatever the checkout
 * currently points to (typically the thank-you page). The new node is left
 * bare on purpose — the auto-deploy queue will fill it with the default
 * upsell starter exactly the same way it fills bare offer nodes that come
 * from the `with-offer` template.
 *
 * Used for Shopify / WooCommerce templates that don't ship with an upsell
 * step but where the merchant asked for one in the wizard.
 */
function injectOfferNode(
    config: NonNullable<CreatedFunnel['config']>,
): NonNullable<CreatedFunnel['config']> {
    const nodes = (config.nodes ?? []).slice();
    const edges: any[] = ((config as any).edges ?? []).slice();

    const checkout = nodes.find((n) => n.type === 'checkout');
    if (!checkout) return config;

    // Edges leaving the checkout node — typically a single edge to thank-you,
    // but we handle multiples by re-pointing each one through the new upsell.
    const outgoingFromCheckout = edges.filter((e) => e.source === checkout.id);
    if (outgoingFromCheckout.length === 0) return config;

    const offerId = `step_${Date.now()}_offer`;
    const checkoutPos = (checkout as { position?: { x?: number; y?: number } }).position ?? {};
    const baseX = typeof checkoutPos.x === 'number' ? checkoutPos.x : 0;
    const baseY = typeof checkoutPos.y === 'number' ? checkoutPos.y : 0;

    const offerNode: FunnelNodeLite = {
        id: offerId,
        name: 'Upsell Offer',
        kind: 'step',
        type: 'offer',
        position: { x: baseX + 400, y: baseY } as any,
        config: {
            path: '/offer/:orderId',
            metadata: (checkout.config as any)?.metadata,
        },
    };

    const remainingEdges: any[] = edges.filter((e) => e.source !== checkout.id);
    for (const outEdge of outgoingFromCheckout) {
        // checkout → offer keeps the original transition condition
        remainingEdges.push({
            id: `edge_${checkout.id}_${offerId}`,
            source: checkout.id,
            target: offerId,
            conditions: outEdge.conditions,
        });
        // offer → original successor — unconditional, mirroring the
        // `with-offer` template shape
        remainingEdges.push({
            id: `edge_${offerId}_${outEdge.target}`,
            source: offerId,
            target: outEdge.target,
            conditions: {},
        });
    }

    // Shift any downstream nodes one column right so the graph stays readable
    // in the visual editor. Best-effort positional cleanup — failure to nudge
    // a node is non-fatal.
    const shiftThreshold = baseX + 400;
    const adjustedNodes = nodes.map((n) => {
        if (n.id === checkout.id) return n;
        const pos = (n as { position?: { x?: number; y?: number } }).position;
        if (!pos || typeof pos.x !== 'number') return n;
        if (pos.x < shiftThreshold) return n;
        return { ...n, position: { ...pos, x: pos.x + 400 } } as FunnelNodeLite;
    });

    return {
        ...config,
        nodes: [...adjustedNodes, offerNode],
        edges: remainingEdges,
    } as NonNullable<CreatedFunnel['config']>;
}

/**
 * Remove every offer-type node from the funnel and re-wire any edges that
 * targeted those nodes to skip them. Edges from the offer node to the next
 * node become the new destination for whatever was pointing at the offer.
 *
 * Example: checkout → offer → thankyou
 *   becomes checkout → thankyou
 */
function stripOfferNodes(
    config: NonNullable<CreatedFunnel['config']>,
    offerNodes: FunnelNodeLite[],
): NonNullable<CreatedFunnel['config']> {
    const offerIds = new Set(offerNodes.map((n) => n.id));
    const edges: any[] = ((config as any).edges ?? []).slice();
    const nodes = (config.nodes ?? []).filter((n) => !offerIds.has(n.id));

    // For each offer node, find every (predecessor → offer) edge and every
    // (offer → successor) edge, then create direct (predecessor → successor)
    // edges that bypass the offer. The original edges touching the offer are
    // dropped.
    const rewiredEdges: any[] = [];
    for (const edge of edges) {
        const targetsOffer = offerIds.has(edge.target);
        const sourceIsOffer = offerIds.has(edge.source);
        if (!targetsOffer && !sourceIsOffer) {
            rewiredEdges.push(edge);
        }
    }

    for (const offer of offerNodes) {
        const incoming = edges.filter((e) => e.target === offer.id);
        const outgoing = edges.filter((e) => e.source === offer.id);
        for (const inEdge of incoming) {
            for (const outEdge of outgoing) {
                rewiredEdges.push({
                    id: `edge_${inEdge.source}_${outEdge.target}`,
                    source: inEdge.source,
                    target: outEdge.target,
                    conditions: inEdge.conditions,
                });
            }
        }
    }

    return {
        ...config,
        nodes,
        edges: rewiredEdges,
    } as NonNullable<CreatedFunnel['config']>;
}

interface SplitConfig {
    splitType: 'geo' | 'weighted';
    variants: Array<{
        label: string;
        percentage: number;
        regions?: string[];
        indexHint: number;
    }>;
}

/**
 * Replace each offer node's single config with N variants that all start
 * with a copy of the just-deployed plugin config. The merchant edits
 * individual variants via the funnel editor afterwards; until then every
 * variant points at the same upsell deployment (which is what makes the
 * funnel validator happy — every variant has a real pluginId / path).
 *
 * Nodes that carry a `splitType` are skipped by `listBareRoutableNodes`
 * so the auto-deploy queue never re-runs against them.
 */
function splitOfferNodes(
    config: NonNullable<CreatedFunnel['config']>,
    offerNodes: FunnelNodeLite[],
    split: SplitConfig,
): NonNullable<CreatedFunnel['config']> {
    const offerIds = new Set(offerNodes.map((n) => n.id));
    const nodes = (config.nodes ?? []).map((n) => {
        if (!offerIds.has(n.id)) return n;
        const deployedConfig = (n.config ?? {}) as Record<string, any>;
        return {
            ...n,
            // Clear single-config when promoting to variants — runtime reads
            // from `variants` once `splitType` is set.
            config: undefined,
            variants: split.variants.map((v) => ({
                id: `${n.id}_variant_${v.indexHint}`,
                label: v.label,
                percentage: v.percentage,
                regions: v.regions,
                // Inherit the deployed plugin config so the funnel validator
                // accepts the save. The merchant can swap any variant out
                // later from the funnel editor.
                config: { ...deployedConfig } as any,
            })),
            splitType: split.splitType,
        } as FunnelNodeLite;
    });
    return { ...config, nodes };
}

// ============================================================================
// Payment transforms (Q4 = manual)
// ============================================================================

/**
 * Pin the checkout step's payment options to card + the selected manual
 * methods. Writes two layers:
 *
 *   - paymentSettings.methods           — translations overlay
 *   - stepConfig.paymentSetupConfig     — enabled/type/label per method
 *
 * The paymentSetupConfig pre-fill also short-circuits Phase 0b
 * (`backfillPaymentSetupConfig`) for these checkouts: that backfill skips
 * any step whose paymentSetupConfig is already non-empty, so the wizard's
 * choice wins over the per-flow processor APM defaults.
 */
const MANUAL_PAYMENT_LABELS: Record<string, string> = {
    zelle: 'Zelle',
    custom_payment: 'Custom Payment',
};

function applyManualPaymentMethods(
    config: NonNullable<CreatedFunnel['config']>,
    methods: string[],
): NonNullable<CreatedFunnel['config']> {
    const methodsRecord: Record<string, { translations: Record<string, never> }> = {};
    const paymentSetupConfig: Record<string, { enabled: boolean; type: string; label: string }> = {
        card: { enabled: true, type: 'card', label: 'Card Payments' },
    };
    for (const method of methods) {
        methodsRecord[method] = { translations: {} };
        paymentSetupConfig[method] = {
            enabled: true,
            type: method,
            label: MANUAL_PAYMENT_LABELS[method] || method,
        };
    }

    const nodes = (config.nodes ?? []).map((n) => {
        // Checkout: pin payment methods (card + selected manuals)
        if (n.type === 'checkout') {
            const existingConfig = (n.config ?? {}) as Record<string, any>;
            const existingStepConfig = (existingConfig.stepConfig ?? {}) as Record<string, any>;
            return {
                ...n,
                config: {
                    ...existingConfig,
                    paymentSettings: {
                        ...existingConfig.paymentSettings,
                        methods: methodsRecord,
                    },
                    stepConfig: {
                        ...existingStepConfig,
                        paymentSetupConfig,
                    },
                },
            } as FunnelNodeLite;
        }
        // Thank you: strip the Studio design picked by assignStudioDesignsToFunnel
        // so the wizard's auto-deploy queue (listBareRoutableNodes filters on
        // instanceId) picks the step up and applies the manual-payment-thankyou
        // starter from resolveStarterOverrides.
        if (n.type === 'thankyou') {
            const existingConfig = (n.config ?? {}) as Record<string, any>;
            const mountPoint = existingConfig.metadata?.mountPoint;
            return {
                ...n,
                config: {
                    path: existingConfig.path || '/thankyou/:orderId',
                    metadata: mountPoint ? { mountPoint } : {},
                },
            } as FunnelNodeLite;
        }
        return n;
    });

    return { ...config, nodes };
}

// ============================================================================
// Extra mount-point steps (Q5 = VIP club, Q6 = subscription portal)
//
// These are standalone funnel steps that mount the native checkout plugin at
// a non-default mount point. They're not connected to the checkout flow via
// edges — the runtime serves them at /club and /portal routes on the funnel's
// domain, completely independent from the main checkout journey.
// ============================================================================

const NATIVE_CHECKOUT_PLUGIN_ID = 'tagada-native';

export interface ExtraMount {
    mountPoint: string;
    nodeName: string;
    path: string;
}

export const EXTRA_MOUNTS: ExtraMount[] = [
    { mountPoint: 'club', nodeName: 'VIP Club', path: '/club' },
    { mountPoint: 'portal', nodeName: 'Customer Portal', path: '/portal' },
];

/**
 * Build a single standalone mount-point step node (club / portal). Shared by
 * the funnel-creation wizard (`applyExtraMountSteps`) and any retrofit that
 * adds a club step to an existing funnel, so the node shape stays in one place.
 */
export function buildExtraMountNode(
    mount: ExtraMount,
    baseX: number,
    baseY: number,
    index = 0,
): FunnelNodeLite {
    return {
        id: `step_${Date.now() + index}_${mount.mountPoint}`,
        name: mount.nodeName,
        kind: 'step',
        type: 'custom',
        position: { x: baseX, y: baseY + 400 + index * 250 } as any,
        config: {
            path: mount.path,
            pluginId: NATIVE_CHECKOUT_PLUGIN_ID,
            metadata: { mountPoint: mount.mountPoint },
        },
    };
}

/** True when a funnel config already contains a step mounted at `mountPoint` (club / portal). */
export function funnelHasMountPoint(
    config: { nodes?: FunnelNodeLite[] } | null | undefined,
    mountPoint: string,
): boolean {
    return (config?.nodes ?? []).some(
        (n) => n.type === 'custom' && (n.config as any)?.metadata?.mountPoint === mountPoint,
    );
}

/** True when a funnel config already contains a club mount-point step. */
export function funnelHasClubStep(
    config: { nodes?: FunnelNodeLite[] } | null | undefined,
): boolean {
    return funnelHasMountPoint(config, 'club');
}

/**
 * Force-enable a VIP order-bump offer on the funnel's checkout step by writing
 * `stepConfig.orderBumps.enabledOfferIds` (custom mode) on the checkout node.
 *
 * - If the checkout already uses `custom` mode, the offer id is appended to its
 *   existing list (other custom-picked bumps are kept).
 * - If it `inherit`s (or has no order-bump config), we seed `custom` with
 *   `fallbackEnabledOfferIds` (the store's currently-enabled bumps) PLUS the VIP
 *   id, so no previously-shown bump disappears.
 *
 * No-op when the funnel has no checkout node ("link … if there is one").
 * Node shape stays here so all funnel-node edits live in one place.
 */
export function addVipBumpToCheckout(
    config: { nodes?: FunnelNodeLite[] } & Record<string, any>,
    vipOfferId: string,
    fallbackEnabledOfferIds: string[],
): { nodes?: FunnelNodeLite[] } & Record<string, any> {
    const nodes = (config.nodes ?? []).map((n) => {
        if (n.type !== 'checkout') return n;
        const existingConfig = (n.config ?? {}) as Record<string, any>;
        const existingStepConfig = (existingConfig.stepConfig ?? {}) as Record<string, any>;
        const ob = existingStepConfig.orderBumps as
            | { mode?: string; enabledOfferIds?: string[] }
            | undefined;
        const base = ob?.mode === 'custom' ? (ob.enabledOfferIds ?? []) : fallbackEnabledOfferIds;
        const enabledOfferIds = Array.from(new Set([...base, vipOfferId]));
        return {
            ...n,
            config: {
                ...existingConfig,
                stepConfig: {
                    ...existingStepConfig,
                    orderBumps: { mode: 'custom', enabledOfferIds },
                },
            },
        } as FunnelNodeLite;
    });
    return { ...config, nodes };
}

/**
 * Add a `landing` step as the entry of the funnel when the merchant picked
 * the "Fresh landing page" traffic source. The default / with-offer
 * funnel templates ship without a landing node — they start at the
 * checkout — so we splice one in:
 *
 *   landing → checkout → ... (existing flow)
 *
 * The landing node is left bare on purpose; the auto-deploy queue will
 * fill it with the velora-landing starter (pinned via
 * `resolveStarterOverrides`). No-op if the funnel already has a landing
 * node or no checkout to attach to.
 */
function applyLandingPrefix(
    config: NonNullable<CreatedFunnel['config']>,
): NonNullable<CreatedFunnel['config']> {
    const nodes = (config.nodes ?? []).slice();
    const edges: any[] = ((config as any).edges ?? []).slice();

    if (nodes.some((n) => n.type === 'landing')) return config;

    const checkout = nodes.find((n) => n.type === 'checkout');
    if (!checkout) return config;

    const landingId = `step_${Date.now()}_landing`;
    const checkoutPos = (checkout as { position?: { x?: number; y?: number } }).position ?? {};
    const baseX = typeof checkoutPos.x === 'number' ? checkoutPos.x : 0;
    const baseY = typeof checkoutPos.y === 'number' ? checkoutPos.y : 0;

    const landingNode: FunnelNodeLite = {
        id: landingId,
        name: 'Landing Page',
        kind: 'step',
        type: 'landing',
        isDefault: true,
        position: { x: baseX - 400, y: baseY } as any,
        config: {
            path: '/',
            metadata: (checkout.config as any)?.metadata,
        },
    };

    // Demote checkout from start page — there's one start page in a funnel.
    const updatedNodes = nodes.map((n) =>
        n.id === checkout.id ? { ...n, isDefault: false } : n,
    );

    edges.push({
        id: `edge_${landingId}_${checkout.id}`,
        source: landingId,
        target: checkout.id,
        conditions: {},
    });

    return {
        ...config,
        nodes: [...updatedNodes, landingNode],
        edges,
    } as NonNullable<CreatedFunnel['config']>;
}

function applyExtraMountSteps(
    config: NonNullable<CreatedFunnel['config']>,
    answers: QuickstartAnswers,
): NonNullable<CreatedFunnel['config']> {
    const wantedMounts = EXTRA_MOUNTS.filter((m) => {
        if (m.mountPoint === 'club') return answers.wantsClub === true;
        if (m.mountPoint === 'portal') return answers.wantsPortal === true;
        return false;
    });
    if (wantedMounts.length === 0) return config;

    const existingNodes = config.nodes ?? [];
    const checkout = existingNodes.find((n) => n.type === 'checkout');
    const baseX = (checkout as any)?.position?.x ?? 0;
    const baseY = (checkout as any)?.position?.y ?? 0;

    const newNodes: FunnelNodeLite[] = wantedMounts.map((m, index) =>
        buildExtraMountNode(m, baseX, baseY, index),
    );

    return {
        ...config,
        nodes: [...existingNodes, ...newNodes],
    };
}
